<template>
    <div class="payment-step text-center">
        <h4 class="mb-3">{{ $t('paymentStep.title') }}</h4>
        <p class="mb-4">{{ $t('paymentStep.description') }}</p>

        <b-card class="mb-4" v-if="subscription">
            <b-row>
                <b-col>
                    <strong>{{ $t('paymentStep.plan') }}</strong>
                    <div>{{ subscription.license_product && subscription.license_product.name ? subscription.license_product.name : subscription.uuid }}</div>
                </b-col>
                <b-col>
                    <strong>{{ $t('paymentStep.price') }}</strong>
                    <div>{{ displayPrice }}</div>
                </b-col>
                <b-col>
                    <strong>{{ $t('paymentStep.status') }}</strong>
                    <div><b-badge variant="warning">{{ $t('paymentStep.unpaid') }}</b-badge></div>
                </b-col>
            </b-row>
        </b-card>

        <b-button
            variant="success"
            size="lg"
            :disabled="inProcess"
            @click="proceedToPayment"
            block
        >
            <b-spinner small v-if="inProcess" class="mr-2"></b-spinner>
            {{ $t('paymentStep.proceedToPayment') }}
        </b-button>

        <b-button
            variant="link"
            size="sm"
            class="mt-3 text-muted"
            :disabled="inProcess"
            @click="dismiss"
        >
            {{ $t('paymentStep.changedMyMind') }}
        </b-button>
    </div>
</template>

<script>
import Checkout from '@/lib/Checkout'

export default {
    props: {
        subscription: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            inProcess: false,
            fetchedPrice: null,
            fetchedCurrency: null
        }
    },

    computed: {
        billingCountry() {
            const user = this.$store.getters.getUser
            const address = user && user.settings && user.settings.billing_address
            return address ? address.country : null
        },

        checkoutCurrency() {
            return this.billingCountry === 'Serbia' ? 'RSD' : (this.subscription ? this.subscription.currency || 'USD' : 'USD')
        },

        displayPrice() {
            if (this.fetchedPrice !== null && this.fetchedCurrency) {
                return this.fetchedPrice.toFixed(2) + ' ' + this.fetchedCurrency
            }
            if (!this.subscription) return ''
            if (this.subscription.human_price && this.subscription.currency) {
                return this.subscription.human_price + ' ' + this.subscription.currency
            }
            return (this.subscription.price / 100) + ' ' + (this.subscription.currency || 'USD')
        }
    },

    watch: {
        billingCountry: {
            immediate: true,
            handler: function() {
                if (this.subscription && this.subscription.license_product) {
                    this.fetchPrice()
                }
            }
        },

        subscription: {
            handler: function(val) {
                if (val && val.license_product) {
                    this.fetchPrice()
                }
            }
        }
    },

    methods: {
        async fetchPrice() {
            if (!this.subscription || !this.subscription.license_product) return
            try {
                const lp = this.subscription.license_product
                const resp = await this.$transactions.getPricesForProduct(
                    'account',
                    lp.id,
                    this.checkoutCurrency,
                    this.subscription.option_code || null
                )
                this.fetchedPrice = resp.data.prorated_amount
                this.fetchedCurrency = this.checkoutCurrency
            } catch (e) {
                // falls back to human_price / price display
            }
        },

        async proceedToPayment() {
            if (!this.subscription) return

            this.inProcess = true

            try {
                const checkout = new Checkout()

                const link = await checkout.getCheckoutLink({
                    product_type: 'subscription',
                    reference: this.subscription.uuid,
                    lang: this.$store.getters.getLocale,
                    currency: this.checkoutCurrency
                })

                window.open(link.data, '_blank')
                this.$emit('paid')
            } catch (err) {
                console.error('Failed to get checkout link:', err)
                const message = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : this.$t('unexpectedError')
                this.$bvToast.toast(message, {
                    variant: 'danger'
                })
            } finally {
                this.inProcess = false
            }
        },

        async dismiss() {
            if (!this.subscription) {
                this.$emit('dismissed')
                return
            }

            this.inProcess = true

            try {
                await this.$transactionApi.post('/subscription/remove', {
                    subscription_id: this.subscription.uuid
                })
                await this.$store.dispatch('fetchSubscriptions')
                this.$emit('dismissed')
            } catch (err) {
                console.error('Failed to remove subscription:', err)
                this.$bvToast.toast(this.$t('unexpectedError'), {
                    variant: 'danger'
                })
            } finally {
                this.inProcess = false
            }
        }
    }
}
</script>
